import React from 'react';

/*
  Animated inline SVG illustrations for the How it works page.

  Inline SVG rather than image files on purpose: they inherit the brand CSS
  variables, stay sharp at any size, cost no extra requests, and the motion is
  plain CSS — so the global prefers-reduced-motion rule in index.css already
  disables all of it for anyone who asks. Keyframes live in index.css next to
  the rest of the bb-* animations; the .bb-art class fixes SVG transform-origin.

  Every illustration is decorative: the surrounding copy carries the meaning,
  so they're aria-hidden rather than labelled.
*/

const RED = 'var(--red)';
const RED_DARK = 'var(--red-dark)';
const RED_MID = 'var(--red-mid)';
const GRAY_200 = 'var(--gray-200)';
const GRAY_300 = 'var(--gray-300)';
const GRAY_400 = 'var(--gray-400)';

function Art({ children, size = 120, viewBox = '0 0 120 120', style }) {
  return (
    <svg
      className="bb-art"
      viewBox={viewBox}
      width={size}
      height={size}
      role="presentation"
      aria-hidden="true"
      style={{ display: 'block', overflow: 'visible', ...style }}
    >
      {children}
    </svg>
  );
}

/* ─── Hero: a drop that keeps filling, with a heartbeat line ───
   `onDark` inverts the palette for the red hero panel, where the normal
   red-on-pink version has almost no contrast. */
export function DropletArt({ size = 132, onDark = false }) {
  const c = onDark
    ? { ring: 'rgba(255,255,255,0.4)', shell: 'rgba(255,255,255,0.14)', outline: 'white', liquid: 'rgba(255,255,255,0.9)', trace: RED_DARK }
    : { ring: RED_MID, shell: 'var(--red-light)', outline: RED, liquid: RED, trace: 'white' };
  const drop = 'M60 14 C60 14 90 50 90 72 A30 30 0 0 1 30 72 C30 50 60 14 60 14 Z';
  const clipId = onDark ? 'bb-drop-clip-dark' : 'bb-drop-clip';

  return (
    <Art size={size}>
      <defs>
        <clipPath id={clipId}><path d={drop} /></clipPath>
      </defs>

      {[0, 1.6].map((delay, i) => (
        <circle
          key={i}
          cx="60" cy="72" r="46"
          fill="none" stroke={c.ring} strokeWidth="1.5"
          style={{ animation: `bb-radar 3.2s ease-out ${delay}s infinite` }}
        />
      ))}

      <path d={drop} fill={c.shell} stroke={c.outline} strokeWidth="2.5" />
      <g clipPath={`url(#${clipId})`}>
        <rect
          x="26" y="60" width="68" height="60" fill={c.liquid}
          style={{ animation: 'bb-fill 3.4s ease-in-out infinite' }}
        />
      </g>
      {/* Heartbeat trace across the filled drop */}
      <path
        d="M38 76 h10 l5 -10 l6 20 l6 -14 h11"
        fill="none" stroke={c.trace} strokeWidth="2.6"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ '--bb-dash': 70, strokeDasharray: 70, animation: 'bb-draw 3.4s ease-in-out infinite' }}
      />
    </Art>
  );
}

/* ─── Matching: a radar sweep over nearby donor pins ─── */
export function MatchRadarArt({ size = 120 }) {
  const donors = [
    { x: 34, y: 44, d: 0.2 }, { x: 86, y: 40, d: 0.9 },
    { x: 30, y: 84, d: 1.5 }, { x: 84, y: 86, d: 0.55 },
    { x: 60, y: 26, d: 1.2 },
  ];
  return (
    <Art size={size}>
      <circle cx="60" cy="62" r="46" fill="var(--red-50)" stroke={GRAY_200} />
      <circle cx="60" cy="62" r="30" fill="none" stroke={GRAY_200} strokeDasharray="3 4" />
      <circle cx="60" cy="62" r="15" fill="none" stroke={GRAY_200} strokeDasharray="3 4" />

      {[0, 1.4].map((delay, i) => (
        <circle
          key={i}
          cx="60" cy="62" r="46"
          fill="none" stroke={RED} strokeWidth="1.6" opacity="0.5"
          style={{ animation: `bb-radar 2.8s ease-out ${delay}s infinite` }}
        />
      ))}

      {/* The request at the centre */}
      <circle cx="60" cy="62" r="8" fill={RED} />
      <circle cx="60" cy="62" r="8" fill="none" stroke={RED} strokeWidth="2"
        style={{ animation: 'bb-radar 2.8s ease-out infinite' }} />

      {donors.map(({ x, y, d }) => (
        <g key={`${x}-${y}`} style={{ animation: `bb-pulse 2.8s ease-in-out ${d}s infinite` }}>
          <circle cx={x} cy={y} r="5.5" fill="white" stroke={RED_DARK} strokeWidth="2" />
          <circle cx={x} cy={y} r="2" fill={RED_DARK} />
        </g>
      ))}
    </Art>
  );
}

/* ─── Alerts: SMS + email landing on a phone ─── */
export function AlertArt({ size = 120 }) {
  return (
    <Art size={size}>
      <rect x="34" y="14" width="52" height="92" rx="9" fill="white" stroke={GRAY_300} strokeWidth="2.5" />
      <rect x="50" y="19" width="20" height="3.5" rx="1.75" fill={GRAY_300} />

      {[
        { y: 32, w: 34, delay: 0 },
        { y: 50, w: 26, delay: 0.8 },
        { y: 68, w: 30, delay: 1.6 },
      ].map(({ y, w, delay }) => (
        <g key={y} style={{ animation: `bb-bubble-in 3.6s ease-out ${delay}s infinite` }}>
          <rect x="41" y={y} width={w} height="12" rx="6" fill="var(--red-light)" stroke={RED_MID} />
          <circle cx="47" cy={y + 6} r="2" fill={RED} />
          <rect x="52" y={y + 4.5} width={w - 16} height="3" rx="1.5" fill={RED_MID} />
        </g>
      ))}

      {/* Envelope flying in */}
      <g style={{ animation: 'bb-float 3s ease-in-out infinite' }}>
        <rect x="84" y="84" width="26" height="18" rx="3" fill="white" stroke={RED} strokeWidth="2" />
        <path d="M84 87 l13 9 l13 -9" fill="none" stroke={RED} strokeWidth="2" strokeLinecap="round" />
      </g>
    </Art>
  );
}

/* ─── Camps: a pin dropping onto a map ─── */
export function CampArt({ size = 120 }) {
  return (
    <Art size={size}>
      <rect x="12" y="20" width="96" height="84" rx="10" fill="var(--gray-50)" stroke={GRAY_200} strokeWidth="2" />
      {/* Roads */}
      <path d="M12 56 h96 M64 20 v84 M12 84 h52" stroke={GRAY_200} strokeWidth="4" strokeLinecap="round" />
      <path d="M24 20 v36 M84 56 v48" stroke={GRAY_200} strokeWidth="3" strokeLinecap="round" />
      {/* Route from the corner to the venue */}
      <path
        d="M26 96 C26 76 44 74 48 62 C52 50 58 46 62 44"
        fill="none" stroke={RED} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="5 5"
        style={{ '--bb-dash': 130, animation: 'bb-draw 3.4s ease-in-out infinite' }}
      />
      <circle cx="26" cy="96" r="4" fill={RED_DARK} />

      <g style={{ animation: 'bb-pin-drop 3.4s ease-out infinite' }}>
        <path
          d="M62 18 A15 15 0 0 1 77 33 C77 45 62 58 62 58 S47 45 47 33 A15 15 0 0 1 62 18 Z"
          fill={RED} stroke={RED_DARK} strokeWidth="1.5"
        />
        <circle cx="62" cy="33" r="5.5" fill="white" />
      </g>
    </Art>
  );
}

/* ─── Verification: a shield with a check drawing itself in ─── */
export function ShieldArt({ size = 120 }) {
  return (
    <Art size={size}>
      <path
        d="M60 14 L96 28 V62 C96 84 80 98 60 106 C40 98 24 84 24 62 V28 Z"
        fill="var(--red-50)" stroke={RED} strokeWidth="2.5"
      />
      <path
        d="M60 22 L88 33 V62 C88 79 76 91 60 98 Z"
        fill={RED} opacity="0.07"
      />
      <path
        d="M44 60 l12 13 l22 -27"
        fill="none" stroke={RED} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"
        style={{ '--bb-dash': 60, strokeDasharray: 60, animation: 'bb-draw 3s ease-in-out infinite' }}
      />
      {[0, 1.5].map((delay, i) => (
        <circle
          key={i}
          cx="60" cy="60" r="44"
          fill="none" stroke={RED_MID} strokeWidth="1.4"
          style={{ animation: `bb-radar 3s ease-out ${delay}s infinite` }}
        />
      ))}
    </Art>
  );
}

/* ─── Certificate: a document with a spinning seal and a light sweep ─── */
export function CertificateArt({ size = 120 }) {
  return (
    <Art size={size}>
      <defs>
        <clipPath id="bb-cert-clip">
          <rect x="18" y="20" width="84" height="66" rx="7" />
        </clipPath>
      </defs>
      <rect x="18" y="20" width="84" height="66" rx="7" fill="white" stroke={GRAY_300} strokeWidth="2.5" />
      <rect x="18" y="20" width="84" height="9" rx="7" fill={RED} />
      {[38, 47, 56].map((y, i) => (
        <rect key={y} x="28" y={y} width={i === 2 ? 34 : 64 - i * 8} height="4" rx="2" fill={GRAY_200} />
      ))}
      {/* The tilt lives on the wrapper: an animated CSS transform on the rect
          itself would replace the attribute transform outright, losing the
          angle the moment the sweep starts. */}
      <g clipPath="url(#bb-cert-clip)">
        <g transform="rotate(14 60 53)">
          <rect
            x="18" y="6" width="30" height="94" fill="white" opacity="0.5"
            style={{ animation: 'bb-sheen 3.6s ease-in-out infinite' }}
          />
        </g>
      </g>

      {/* Seal */}
      <g style={{ animation: 'bb-float 3.4s ease-in-out infinite' }}>
        <g style={{ animation: 'bb-spin-slow 12s linear infinite' }}>
          <circle cx="84" cy="76" r="17" fill={RED} />
          <circle cx="84" cy="76" r="17" fill="none" stroke="white" strokeWidth="1.5" strokeDasharray="4 4" />
        </g>
        <path d="M78 76 l4.5 5 l8 -10" fill="none" stroke="white" strokeWidth="3"
          strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {/* Ribbon tails */}
      <path d="M76 92 l-5 16 l8 -5 l7 5 l-5 -16 z" fill={RED_DARK} />
    </Art>
  );
}

/* ─── Cooldown: a dial sweeping through the recovery window ─── */
export function CooldownArt({ size = 120, label = '56' }) {
  const r = 40;
  const circumference = 2 * Math.PI * r;
  return (
    <Art size={size}>
      <circle cx="60" cy="60" r={r} fill="none" stroke={GRAY_200} strokeWidth="9" />
      {/* Rotated in CSS, not via the SVG transform attribute: .bb-art sets
          transform-box: fill-box, which would make rotate(-90 60 60) resolve
          its centre against the shape's own box and fling the arc off-canvas. */}
      <circle
        cx="60" cy="60" r={r}
        fill="none" stroke={RED} strokeWidth="9" strokeLinecap="round"
        style={{
          '--bb-dash': circumference,
          strokeDasharray: circumference,
          transform: 'rotate(-90deg)',
          animation: 'bb-sweep 4s ease-in-out infinite',
        }}
      />
      <text
        x="60" y="57" textAnchor="middle"
        style={{ fontFamily: 'var(--font)', fontSize: '24px', fontWeight: 900, fill: 'var(--gray-900)' }}
      >
        {label}
      </text>
      <text
        x="60" y="73" textAnchor="middle"
        style={{ fontFamily: 'var(--font)', fontSize: '10px', fontWeight: 600, fill: GRAY_400, letterSpacing: '0.08em' }}
      >
        DAYS
      </text>
    </Art>
  );
}

/* ─── Handshake: donor meets recipient (the response step) ─── */
export function HandoffArt({ size = 120 }) {
  return (
    <Art size={size}>
      {/* Two people */}
      {[{ cx: 28, fill: RED }, { cx: 92, fill: RED_DARK }].map(({ cx, fill }) => (
        <g key={cx}>
          <circle cx={cx} cy="42" r="11" fill="white" stroke={fill} strokeWidth="2.5" />
          <path
            d={`M${cx - 16} 88 C${cx - 16} 68 ${cx + 16} 68 ${cx + 16} 88 Z`}
            fill="white" stroke={fill} strokeWidth="2.5"
          />
        </g>
      ))}
      {/* The drop travelling between them */}
      <path d="M42 70 C52 58 68 58 78 70" fill="none" stroke={GRAY_300} strokeWidth="2" strokeDasharray="4 4" />
      <g style={{ animation: 'bb-drip 2.6s ease-in-out infinite' }}>
        <path d="M60 50 C60 50 68 60 68 65 A8 8 0 0 1 52 65 C52 60 60 50 60 50 Z" fill={RED} />
      </g>
      <path
        d="M46 96 h28"
        stroke={RED_MID} strokeWidth="3" strokeLinecap="round"
        style={{ '--bb-dash': 28, strokeDasharray: 28, animation: 'bb-draw 2.6s ease-in-out infinite' }}
      />
    </Art>
  );
}
